import { html, nothing, type TemplateResult } from "lit";

export function tryParseJsonLoose(text: string): unknown | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) {
    return null;
  }
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }
}

export function summarizeJsonKeys(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return `array(${value.length})`;
  }
  if (typeof value === "object") {
    const keys = Object.keys(value as Record<string, unknown>);
    if (keys.length === 0) {
      return "{}";
    }
    const preview = keys.slice(0, 4).join(", ");
    return keys.length > 4 ? `${preview}, …` : preview;
  }
  return "";
}

export function formatJsonPretty(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export type StructuredToolBodyOpts = {
  expandedDefault?: boolean;
  onCopy?: () => void;
};

/** Renders JSON tool output as a compact summary + expandable `<pre>` block with copy. */
export function renderStructuredJsonBody(
  rawText: string,
  opts: StructuredToolBodyOpts = {},
): TemplateResult | typeof nothing {
  const parsed = tryParseJsonLoose(rawText);
  if (parsed === null) {
    return nothing;
  }

  const pretty = formatJsonPretty(parsed);
  const summary = summarizeJsonKeys(parsed);

  return html`
    <div class="chat-structured-json">
      <div class="chat-structured-json__summary muted mono">${summary || "JSON"}</div>
      <details class="chat-structured-json__details" ?open=${opts.expandedDefault ?? false}>
        <summary class="chat-structured-json__toggle">Formatted JSON</summary>
        <pre class="chat-structured-json__pre mono">${pretty}</pre>
      </details>
      <button
        type="button"
        class="btn chat-structured-json__copy"
        @click=${() => {
          void copyToClipboard(pretty);
          opts.onCopy?.();
        }}
      >
        Copy JSON
      </button>
    </div>
  `;
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
}
